import React, { useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import Header from '../../components/ui/Header';
import Sidebar from '../../components/ui/Sidebar';
import Breadcrumb from '../../components/ui/Breadcrumb';
import Icon from '../../components/AppIcon';
import Button from '../../components/ui/Button';
import { astraAPI } from '../../services/astraApi';
import { SkeletonCard, SkeletonList } from '../../components/ui/Skeleton';
import useAutoRefresh from '../../hooks/useAutoRefresh';

/**
 * Ultra-Fast Trading Page
 *
 * 🔧 PRODUCTION VERSION (2025-12-28): Enhanced with ASTRA Architecture Showcase
 *
 * Ultra-Fast Engine Capabilities (from Knowledge docs):
 * - Decision Time: <1 second (typically 2.64ms)
 * - Numba-optimized indicators: 10-20 microseconds
 * - 20-25 high-volatility cryptos in rotation
 * - 60-second analysis cycles
 * - NO LLM calls - pure technical analysis
 */

// Ultra-Fast Engine Specs
const ENGINE_SPECS = {
  decisionTime: '2.64ms',
  indicatorCalc: '10-20μs',
  cycleTime: '60s',
  watchlistSize: '20-25',
  coverage: '100+ cryptos/5min'
};

const UltraFastTrading = () => {
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [executionData, setExecutionData] = useState([]);
  const [performanceStats, setPerformanceStats] = useState(null);
  const [executionMetrics, setExecutionMetrics] = useState(null);
  const [ultraFastMetrics, setUltraFastMetrics] = useState(null);
  const [loading, setLoading] = useState(true);
  // 🔧 2025-12-30 FIX: Prevent flickering on refresh - only show skeleton on INITIAL load
  const [isInitialLoad, setIsInitialLoad] = useState(true);
  const [error, setError] = useState(null);
  const [isLive, setIsLive] = useState(true);
  const [wsConnected, setWsConnected] = useState(false);

  // Auto-refresh using custom hook (every 5 seconds for ultra-fast view)
  const { refreshCount } = useAutoRefresh({
    intervalMs: 5000,
    enabled: true,
    onRefresh: fetchTradingData
  });

  useEffect(() => {
    fetchTradingData();
    setupRealTimeUpdates();
  }, []);

  const fetchTradingData = async () => {
    try {
      // 🔧 2025-12-30 FIX: Only show loading skeleton on INITIAL load
      if (isInitialLoad) {
        setLoading(true);
      }
      setError(null);

      // 🔧 2025-12-30 FIX: Use correct portfolio_id (1453) and ultra-fast metrics endpoint
      const [recentOrders, metrics, performance, ultraFastMetrics] = await Promise.all([
        astraAPI.getRecentOrders(20).catch(err => {
          console.warn('⚠️ getRecentOrders failed:', err);
          return []; // Return empty array on error
        }),
        astraAPI.getExecutionMetrics().catch(err => {  // Uses default portfolio_id=1453
          console.warn('⚠️ getExecutionMetrics failed:', err);
          return null; // Return null on error
        }),
        astraAPI.getExecutionPerformance().catch(err => {  // Uses default portfolio_id=1453
          console.warn('⚠️ getExecutionPerformance failed:', err);
          return null; // Return null on error
        }),
        astraAPI.getUltraFastTradingMetrics().catch(err => {
          console.warn('⚠️ getUltraFastTradingMetrics failed:', err);
          return null; // Return null on error - endpoint created 2025-12-30
        })
      ]);

      console.log('⚡ Ultra-Fast Trading Data:', {
        orders: recentOrders?.length || 0,
        metrics: metrics ? 'present' : 'missing',
        performance: performance ? 'present' : 'missing',
        ultraFastMetrics: ultraFastMetrics ? 'present' : 'missing'
      });

      // Transform orders to execution format
      const executions = (recentOrders || []).map(order => ({
        id: order.order_id,
        symbol: order.symbol,
        action: order.side.toUpperCase(),
        quantity: order.quantity,
        price: order.target_price || 0,
        executedPrice: order.execution_price || order.target_price || 0,
        executionTime: (order.execution_time_seconds || 0) * 1000, // Convert to ms
        slippage: order.slippage || 0,
        timestamp: new Date(order.timestamp),
        venue: order.venue,
        status: order.status
      }));

      setExecutionData(executions);
      setExecutionMetrics(metrics);
      setPerformanceStats(performance);
      setUltraFastMetrics(ultraFastMetrics);

    } catch (err) {
      console.error('Error fetching ultra-fast trading data:', err);
      setError(`Failed to load trading data: ${err.message}`);
    } finally {
      setLoading(false);
      // 🔧 FIX: Mark initial load complete - subsequent refreshes won't show skeleton
      setIsInitialLoad(false);
    }
  };

  const setupRealTimeUpdates = () => {
    // 🔧 PRODUCTION FIX: Use WebSocket for REAL-TIME updates instead of random data
    try {
      const ws = astraAPI.subscribeToTradingActivity((data) => {
        console.log('⚡ Real-time trading update:', data);

        if (data.type === 'order_update' || data.type === 'trade') {
          // Transform real update to execution format
          const newExecution = {
            id: data.order_id || Date.now(),
            symbol: data.symbol,
            action: data.side?.toUpperCase() || data.action,
            quantity: data.quantity,
            price: data.price || data.target_price,
            executedPrice: data.execution_price || data.price,
            executionTime: (data.execution_time_seconds || 0) * 1000,
            slippage: data.slippage || 0,
            timestamp: new Date(data.timestamp || Date.now()),
            venue: data.venue,
            status: data.status
          };

          // Prepend new execution, keep last 20
          setExecutionData(prev => [newExecution, ...prev.slice(0, 19)]);

          // Update metrics count
          setExecutionMetrics(prev => ({
            ...prev,
            total_orders: (prev?.total_orders || 0) + 1
          }));

          setWsConnected(true);
        }
      });

      return () => {
        if (ws && ws.close) ws.close();
      };
    } catch (error) {
      console.error('Failed to setup WebSocket:', error);
      setWsConnected(false);
    }
  };

  const getExecutionTimeColor = (time) => {
    if (time <= 100) return 'success';    // < 100ms is excellent
    if (time <= 500) return 'warning';     // < 500ms is good
    return 'error';                        // > 500ms needs attention
  };

  const getSlippageColor = (slippage) => {
    const absSlippage = Math.abs(slippage);
    if (absSlippage <= 0.05) return 'success';
    if (absSlippage <= 0.1) return 'warning';
    return 'error';
  };

  const formatExecutionTime = (time) => {
    if (time < 1) return `${(time * 1000).toFixed(1)}μs`;
    if (time < 1000) return `${time.toFixed(1)}ms`;
    return `${(time / 1000).toFixed(2)}s`;
  };

  const formatTimestamp = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleTimeString('en-IN', {
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
      fractionalSecondDigits: 3
    });
  };

  // 🔧 2025-12-30 FIX: Only show skeleton on INITIAL load, not on refreshes
  if (loading && isInitialLoad) {
    return (
      <div className="min-h-screen bg-background">
        <Header />
        <Sidebar
          isCollapsed={sidebarCollapsed}
          onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
        />
        <main className={`pt-16 transition-all duration-300 ${sidebarCollapsed ? 'ml-16' : 'ml-64'}`}>
          <div className="p-6">
            <motion.div initial={{ opacity: 0 }} animate={{ opacity: 1 }} className="mb-6">
              <h1 className="text-2xl font-bold text-foreground">Ultra-Fast Trading</h1>
              <p className="text-muted-foreground mt-1">Loading high-frequency trading data...</p>
            </motion.div>
            <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-6">
              <SkeletonCard className="h-20" />
              <SkeletonCard className="h-20" />
              <SkeletonCard className="h-20" />
              <SkeletonCard className="h-20" />
              <SkeletonCard className="h-20" />
            </div>
            <SkeletonCard className="h-64" />
          </div>
        </main>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-background">
      <Header />
      <Sidebar
        isCollapsed={sidebarCollapsed}
        onToggleCollapse={() => setSidebarCollapsed(!sidebarCollapsed)}
      />
      <main className={`pt-16 transition-all duration-300 ${
        sidebarCollapsed ? 'ml-16' : 'ml-64'
      }`}>
        <div className="p-6">
          {/* Header Section */}
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3 }}
            className="mb-6"
          >
            <Breadcrumb />
            <div className="flex items-center justify-between">
              <div>
                <h1 className="text-2xl font-bold text-foreground">Ultra-Fast Trading Engine</h1>
                <p className="text-muted-foreground mt-1">
                  Sub-millisecond decisions with Numba-optimized technical analysis
                </p>
              </div>
              <div className="flex items-center space-x-4">
                {/* Real-time connection indicator */}
                <div className="flex items-center space-x-2">
                  <div className={`w-3 h-3 rounded-full ${wsConnected ? 'bg-success animate-live-pulse' : 'bg-warning'}`}></div>
                  <span className="text-sm font-medium text-muted-foreground">
                    {wsConnected ? 'Live Updates' : 'Polling'}
                  </span>
                </div>

                <Button
                  variant="outline"
                  size="sm"
                  onClick={fetchTradingData}
                  iconName="RefreshCw"
                  iconSize={16}
                >
                  Refresh
                </Button>
              </div>
            </div>
          </motion.div>

          {/* Engine Specs Showcase */}
          <motion.div
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.3, delay: 0.1 }}
            className="grid grid-cols-2 md:grid-cols-5 gap-4 mb-6"
          >
            <div className="bg-gradient-to-br from-yellow-500/10 to-orange-500/10 border border-yellow-500/20 rounded-lg p-4 glass-card-hover">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Zap" size={16} className="text-yellow-500" />
                <span className="text-xs text-muted-foreground">Decision Time</span>
              </div>
              <div className="text-xl font-bold text-foreground">{ENGINE_SPECS.decisionTime}</div>
            </div>
            <div className="bg-gradient-to-br from-blue-500/10 to-purple-500/10 border border-blue-500/20 rounded-lg p-4 glass-card-hover">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Cpu" size={16} className="text-blue-500" />
                <span className="text-xs text-muted-foreground">Indicator Calc</span>
              </div>
              <div className="text-xl font-bold text-foreground">{ENGINE_SPECS.indicatorCalc}</div>
            </div>
            <div className="bg-gradient-to-br from-green-500/10 to-emerald-500/10 border border-green-500/20 rounded-lg p-4 glass-card-hover">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Clock" size={16} className="text-green-500" />
                <span className="text-xs text-muted-foreground">Cycle Time</span>
              </div>
              <div className="text-xl font-bold text-foreground">{ENGINE_SPECS.cycleTime}</div>
            </div>
            <div className="bg-gradient-to-br from-pink-500/10 to-rose-500/10 border border-pink-500/20 rounded-lg p-4 glass-card-hover">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="List" size={16} className="text-pink-500" />
                <span className="text-xs text-muted-foreground">Watchlist</span>
              </div>
              <div className="text-xl font-bold text-foreground">{ENGINE_SPECS.watchlistSize}</div>
            </div>
            <div className="bg-gradient-to-br from-cyan-500/10 to-teal-500/10 border border-cyan-500/20 rounded-lg p-4 glass-card-hover">
              <div className="flex items-center space-x-2 mb-2">
                <Icon name="Gauge" size={16} className="text-cyan-500" />
                <span className="text-xs text-muted-foreground">Coverage</span>
              </div>
              <div className="text-lg font-bold text-foreground">{ENGINE_SPECS.coverage}</div>
            </div>
          </motion.div>

          {/* Error Banner */}
          {error && (
            <div className="mb-6 bg-error/10 border border-error/20 rounded-lg p-4">
              <div className="flex items-center space-x-2">
                <Icon name="AlertTriangle" size={20} className="text-error" />
                <p className="text-sm text-error">{error}</p>
                <button
                  onClick={fetchTradingData}
                  className="ml-auto px-3 py-1 text-xs bg-error text-white rounded hover:bg-error/90"
                >
                  Retry
                </button>
              </div>
            </div>
          )}

          {/* Performance Metrics Cards */}
          {executionMetrics ? (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-6">
              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Total Executions</span>
                  <Icon name="Zap" size={16} className="text-primary" />
                </div>
                <div className="text-2xl font-bold text-foreground">
                  {executionMetrics.total_orders || 0}
                </div>
                <div className="text-xs text-success mt-1">
                  Last 24 hours
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Success Rate</span>
                  <Icon name="Target" size={16} className="text-success" />
                </div>
                <div className="text-2xl font-bold text-success">
                  {executionMetrics.success_rate?.toFixed(1) || 0}%
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  {executionMetrics.completed_orders || 0} completed
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Avg Execution</span>
                  <Icon name="Clock" size={16} className="text-warning" />
                </div>
                <div className="text-2xl font-bold text-foreground">
                  {formatExecutionTime((executionMetrics.avg_execution_time_seconds || 0) * 1000)}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Per trade
                </div>
              </div>

              <div className="bg-card border border-border rounded-lg p-4">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm text-muted-foreground">Failed Orders</span>
                  <Icon name="AlertTriangle" size={16} className="text-error" />
                </div>
                <div className="text-2xl font-bold text-error">
                  {executionMetrics.failed_orders || 0}
                </div>
                <div className="text-xs text-muted-foreground mt-1">
                  Retry rate: {executionMetrics.failed_orders > 0
                    ? ((executionMetrics.failed_orders / executionMetrics.total_orders) * 100).toFixed(1)
                    : 0}%
                </div>
              </div>
            </div>
          ) : !loading ? (
            <div className="bg-card border border-border rounded-lg p-8 mb-6">
              <div className="text-center">
                <Icon name="Database" size={48} className="text-muted-foreground mx-auto mb-4" />
                <h3 className="text-lg font-semibold text-foreground mb-2">No Execution Metrics Available</h3>
                <p className="text-sm text-muted-foreground mb-4">
                  Execution metrics will appear here once trades are executed through the ultra-fast trading path.
                </p>
                <p className="text-xs text-muted-foreground">
                  This page tracks sub-100ms executions. Metrics include total executions, success rate, average execution time, and failed orders.
                </p>
              </div>
            </div>
          ) : null}

          {/* Recent Executions Table */}
          <div className="bg-card border border-border rounded-lg p-6">
            <div className="flex items-center justify-between mb-4">
              <div>
                <h2 className="text-lg font-semibold text-foreground">Recent Executions</h2>
                <p className="text-sm text-muted-foreground">Last 20 ultra-fast trades</p>
              </div>
              <div className="text-sm text-muted-foreground">
                Showing {executionData.length} executions
              </div>
            </div>

            {loading ? (
              <div className="flex items-center justify-center py-12">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary"></div>
              </div>
            ) : executionData.length === 0 ? (
              <div className="flex items-center justify-center py-12">
                <div className="text-center">
                  <Icon name="Zap" size={24} className="text-muted-foreground mx-auto mb-2" />
                  <p className="text-sm text-muted-foreground">No recent executions</p>
                </div>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="border-b border-border">
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Time</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Symbol</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Action</th>
                      <th className="text-right p-3 text-sm font-medium text-muted-foreground">Quantity</th>
                      <th className="text-right p-3 text-sm font-medium text-muted-foreground">Price</th>
                      <th className="text-right p-3 text-sm font-medium text-muted-foreground">Exec Price</th>
                      <th className="text-right p-3 text-sm font-medium text-muted-foreground">Exec Time</th>
                      <th className="text-right p-3 text-sm font-medium text-muted-foreground">Slippage</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Venue</th>
                      <th className="text-left p-3 text-sm font-medium text-muted-foreground">Status</th>
                    </tr>
                  </thead>
                  <tbody>
                    {executionData.map((exec, index) => (
                      <tr
                        key={exec.id || index}
                        className="border-b border-border last:border-b-0 hover:bg-muted/30 transition-colors"
                      >
                        <td className="p-3 text-sm text-muted-foreground font-mono">
                          {formatTimestamp(exec.timestamp)}
                        </td>
                        <td className="p-3">
                          <span className="text-sm font-medium text-foreground">{exec.symbol}</span>
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            exec.action === 'BUY'
                              ? 'bg-green-100 text-green-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {exec.action}
                          </span>
                        </td>
                        <td className="p-3 text-right text-sm text-foreground">
                          {exec.quantity?.toFixed(4) || '0.0000'}
                        </td>
                        <td className="p-3 text-right text-sm text-foreground font-mono">
                          {exec.price?.toFixed(2) || '0.00'}
                        </td>
                        <td className="p-3 text-right text-sm text-foreground font-mono">
                          {exec.executedPrice?.toFixed(2) || '0.00'}
                        </td>
                        <td className="p-3 text-right">
                          <span className={`text-sm font-medium ${
                            getExecutionTimeColor(exec.executionTime) === 'success' ? 'text-green-600' :
                            getExecutionTimeColor(exec.executionTime) === 'warning' ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {formatExecutionTime(exec.executionTime)}
                          </span>
                        </td>
                        <td className="p-3 text-right">
                          <span className={`text-sm font-medium ${
                            getSlippageColor(exec.slippage) === 'success' ? 'text-green-600' :
                            getSlippageColor(exec.slippage) === 'warning' ? 'text-yellow-600' :
                            'text-red-600'
                          }`}>
                            {exec.slippage >= 0 ? '+' : ''}{exec.slippage?.toFixed(2) || '0.00'}%
                          </span>
                        </td>
                        <td className="p-3 text-sm text-muted-foreground">
                          {exec.venue || 'N/A'}
                        </td>
                        <td className="p-3">
                          <span className={`inline-flex px-2 py-1 rounded-full text-xs font-medium ${
                            exec.status === 'executed' || exec.status === 'completed'
                              ? 'bg-green-100 text-green-700'
                              : exec.status === 'pending'
                              ? 'bg-yellow-100 text-yellow-700'
                              : 'bg-red-100 text-red-700'
                          }`}>
                            {exec.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Performance Distribution Charts */}
          {performanceStats && performanceStats.data_source && (
            <div className="mt-6 grid grid-cols-1 lg:grid-cols-2 gap-6">
              {/* Execution Time Distribution */}
              <div className="bg-card border border-border rounded-lg p-6">
                <h3 className="text-lg font-semibold text-foreground mb-4">Execution Performance</h3>
                <div className="space-y-3">
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Avg Execution Time</span>
                    <span className="text-sm font-medium text-foreground">
                      {formatExecutionTime((performanceStats.avg_execution_time_seconds || 0) * 1000)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Min Execution Time</span>
                    <span className="text-sm font-medium text-success">
                      {formatExecutionTime((performanceStats.min_execution_time_seconds || 0) * 1000)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Max Execution Time</span>
                    <span className="text-sm font-medium text-error">
                      {formatExecutionTime((performanceStats.max_execution_time_seconds || 0) * 1000)}
                    </span>
                  </div>
                  <div className="flex justify-between items-center">
                    <span className="text-sm text-muted-foreground">Avg Slippage</span>
                    <span className="text-sm font-medium text-foreground">
                      {performanceStats.avg_slippage_percent?.toFixed(4) || '0.0000'}%
                    </span>
                  </div>
                  <div className="flex justify-between items-center pt-2 border-t border-border">
                    <span className="text-sm text-muted-foreground">Data Source</span>
                    <span className="text-xs font-medium text-primary">
                      {performanceStats.data_source} (Real)
                    </span>
                  </div>
                </div>
              </div>

              {/* Success Rate & Stats */}
              {executionMetrics && (
                <div className="bg-card border border-border rounded-lg p-6">
                  <h3 className="text-lg font-semibold text-foreground mb-4">Execution Statistics</h3>
                  <div className="space-y-3">
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Total Executions</span>
                      <span className="text-sm font-medium text-foreground">
                        {executionMetrics.total_orders || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Completed</span>
                      <span className="text-sm font-medium text-success">
                        {executionMetrics.completed_orders || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Pending</span>
                      <span className="text-sm font-medium text-warning">
                        {executionMetrics.pending_orders || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center">
                      <span className="text-sm text-muted-foreground">Failed</span>
                      <span className="text-sm font-medium text-error">
                        {executionMetrics.failed_orders || 0}
                      </span>
                    </div>
                    <div className="flex justify-between items-center pt-2 border-t border-border">
                      <span className="text-sm text-muted-foreground">Avg Profit/Trade</span>
                      <span className={`text-sm font-medium ${
                        executionMetrics.avg_profit_per_trade >= 0 ? 'text-green-600' : 'text-red-600'
                      }`}>
                        {executionMetrics.avg_profit_per_trade >= 0 ? '+' : ''}
                        ₹{executionMetrics.avg_profit_per_trade?.toFixed(2) || '0.00'}
                      </span>
                    </div>
                  </div>
                </div>
              )}
            </div>
          )}

          {/* Info Banner about Ultra-Fast Trading */}
          <div className="mt-6 bg-primary/10 border border-primary/20 rounded-lg p-4">
            <div className="flex items-start space-x-3">
              <Icon name="Info" size={20} className="text-primary mt-0.5" />
              <div className="flex-1">
                <h4 className="text-sm font-semibold text-primary mb-1">Ultra-Fast Trading Engine</h4>
                <p className="text-xs text-muted-foreground">
                  This page shows executions from the ultra-fast trading path (&lt;100ms target).
                  Data is sourced from TradeLog database and updated in real-time via WebSocket.
                  Execution times below 100ms are considered excellent (green), 100-500ms are good (yellow),
                  and above 500ms need optimization (red).
                </p>
              </div>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
};

export default UltraFastTrading;
